import { useCallback, useMemo } from "react";
import { useInfiniteQuery, type InfiniteData } from "react-query";
import {
  OrderBy,
  type AdBriefAssetIdsQueryVariables,
  type AdBriefAssetsListQuery,
  type AdBriefAssetsListQueryVariables,
  type AdBriefAssetsOrderBy,
  useAdBriefAssetIdsQuery,
  useAdBriefAssetsListQuery,
} from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";

export type AdAssetItem = AdBriefAssetsListQuery["ad_brief_assets"][number];

/**
 * Rows in the first page. Kept small so the grid paints quickly — an ad row
 * carries its markup, so a wide first page is the slowest thing on the page.
 */
export const AD_ASSETS_FIRST_PAGE_SIZE = 36;

/**
 * Rows in every page after the first. Larger, because by then the user is
 * scrolling and the cost that matters is running out of ads, not first paint.
 */
export const AD_ASSETS_NEXT_PAGE_SIZE = 72;

/** Most recently edited first, for the grids that offer no sort menu. */
export const DEFAULT_AD_ASSETS_ORDER_BY: AdBriefAssetsOrderBy[] = [
  { updated_at: OrderBy.Desc },
];

/**
 * Cache key for one growing list. The ordering belongs in it: two sorts of the
 * same scope are different lists, and sharing a key would page one into the
 * other. Callers that reach into the cache by hand (the board's optimistic
 * move) build their key here rather than repeating the shape.
 */
export function adAssetsQueryKey(
  where: AdBriefAssetsListQueryVariables["where"],
  orderBy: AdBriefAssetsOrderBy[] = DEFAULT_AD_ASSETS_ORDER_BY,
) {
  return ["AdBriefAssetsInfinite", where, orderBy] as const;
}

// Base polling cadence for new/changed ads. The governed hook backs off when
// nothing changes and pauses while the tab is hidden, so this only costs a
// fresh query while the grid is actively changing (e.g. a background
// generation landing new ads).
const POLL_BASE_MS = 10_000;

type UseAdAssetsInfiniteArgs = {
  /** The surface's ad_brief_assets where (folder/view scope + filters). */
  where: AdBriefAssetsListQueryVariables["where"];
  /** The grid's ordering, from its sort menu. */
  orderBy?: AdBriefAssetsOrderBy[];
  enabled?: boolean;
  /**
   * Keep the interval refetch running. Set false where the grid is mounted but
   * invisible: since every loaded page is refetched each tick, polling a list
   * nobody can see is the most expensive thing an occluded grid does.
   */
  poll?: boolean;
};

type UseAdAssetsInfiniteResult = {
  /** Deduped, flattened assets across all loaded pages. */
  assets: AdAssetItem[];
  /** Total matching assets for the where clause (from the aggregate). */
  totalCount: number;
  /** Whether another page is available. Feed to the grid's trailing skeletons. */
  hasMore: boolean;
  /** Fetch the next page; no-ops while a page fetch is already in flight. */
  loadMore: () => void;
  isLoading: boolean;
  /** True while showing kept-previous data after the where changed. */
  isPreviousData: boolean;
  /** The last fetch failed; cleared by the next one that succeeds. */
  isError: boolean;
};

/** Rows to request at a given offset: the small first page, then large ones. */
export function adAssetsPageSize(offset: number): number {
  return offset === 0 ? AD_ASSETS_FIRST_PAGE_SIZE : AD_ASSETS_NEXT_PAGE_SIZE;
}

/**
 * The next `offset` to request, or undefined when the last page came back
 * short — which is how the query knows it has reached the end.
 *
 * The next offset is the number of rows already fetched, not
 * `pages.length * pageSize` — pages are no longer all the same size.
 */
export function adAssetsNextPageParam(
  lastPage: AdBriefAssetsListQuery,
  pages: AdBriefAssetsListQuery[],
): number | undefined {
  const loaded = pages.reduce(
    (total, page) => total + page.ad_brief_assets.length,
    0,
  );
  const requested = adAssetsPageSize(loaded - lastPage.ad_brief_assets.length);
  return lastPage.ad_brief_assets.length >= requested ? loaded : undefined;
}

/**
 * Ids of the ads matching `where`, in `orderBy` order, up to `limit`, in one
 * request, so an export reaches ads the grid hasn't loaded yet.
 */
export async function fetchAdAssetIds({
  where,
  orderBy = DEFAULT_AD_ASSETS_ORDER_BY,
  limit,
}: {
  where: AdBriefAssetIdsQueryVariables["where"];
  orderBy?: AdBriefAssetsOrderBy[];
  limit: number;
}): Promise<string[]> {
  const { ad_brief_assets: rows } = await useAdBriefAssetIdsQuery.fetcher({
    where,
    // The id breaks ties, so ads sharing a timestamp keep one order across exports.
    orderBy: [...orderBy, { id: OrderBy.Asc }],
    limit,
  })();
  return rows.map((row) => row.id);
}

// Pagination overlap: an ad that's edited, renamed, or newly generated while
// later pages are loaded shifts rows across the LIMIT boundaries under every
// ordering, and the same asset can come back in two pages. Dedupe so the grid
// renders each id once.
export function flattenAssetPages(
  pages: AdBriefAssetsListQuery[],
): AdAssetItem[] {
  const byId = new Map<string, AdAssetItem>();
  for (const page of pages) {
    for (const asset of page.ad_brief_assets) {
      if (!byId.has(asset.id)) {
        byId.set(asset.id, asset);
      }
    }
  }
  return [...byId.values()];
}

// Shared infinite-scrolling data source for the asset-scoped ad grids
// (all-ads / favorites / my-ads / folder detail). One growing list per where
// clause — changing the where (view, folder, filters) starts a fresh query
// while keepPreviousData keeps the old grid on screen until page one lands.
export function useAdAssetsInfinite({
  where,
  orderBy = DEFAULT_AD_ASSETS_ORDER_BY,
  enabled = true,
  poll = true,
}: UseAdAssetsInfiniteArgs): UseAdAssetsInfiniteResult {
  // Note: interval refetches re-fetch every loaded page, not just the head.
  // The compareKey (total count + head row) backs polling off whenever the
  // list is quiet, which keeps that acceptable for typical page depths.
  const { refetchInterval } = useGovernedRefetchInterval<
    InfiniteData<AdBriefAssetsListQuery>
  >({
    baseMs: POLL_BASE_MS,
    compareKey: (data) => {
      const head = data?.pages[0];
      return [
        head?.ad_brief_assets_aggregate.aggregate?.count ?? 0,
        head?.ad_brief_assets[0]?.id ?? null,
        head?.ad_brief_assets[0]?.updated_at ?? null,
      ];
    },
  });

  const query = useInfiniteQuery<AdBriefAssetsListQuery>({
    queryKey: adAssetsQueryKey(where, orderBy),
    queryFn: ({ pageParam = 0 }) =>
      useAdBriefAssetsListQuery.fetcher({
        where,
        orderBy,
        limit: adAssetsPageSize(pageParam as number),
        offset: pageParam as number,
      })(),
    getNextPageParam: adAssetsNextPageParam,
    enabled,
    keepPreviousData: true,
    refetchOnMount: "always",
    refetchInterval: poll ? refetchInterval : false,
  });

  const assets = useMemo(
    () => flattenAssetPages(query.data?.pages ?? []),
    [query.data?.pages],
  );

  const totalCount =
    query.data?.pages[0]?.ad_brief_assets_aggregate.aggregate?.count ?? 0;

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    assets,
    totalCount,
    hasMore: Boolean(hasNextPage),
    loadMore,
    isLoading: query.isLoading,
    isPreviousData: query.isPreviousData,
    isError: query.isError,
  };
}
